import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  SENTRY_CONTEXT_KEYS,
  SENTRY_HEADER_KEYS,
  SENTRY_TAG_KEYS,
} from './sentry-constants';
import { sanitizeHeaders, sanitizeRequestData, sanitizeUrl } from './sentry-utils';

export const SENTRY_HTTP_ERROR_SILENT = new HttpContextToken<boolean>(() => false);
const SENTRY_INGEST_HOST = getSentryIngestHost(environment.sentry?.dsn);

@Injectable()
export class SentryHttpErrorInterceptor implements HttpInterceptor {
  constructor() { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldIgnoreError(error, req)) {
          return throwError(() => error);
        }
        if (!req.context.get(SENTRY_HTTP_ERROR_SILENT)) {
          this.captureHttpError(error, req);
        }
        return throwError(() => error);
      })
    );
  }

  private captureHttpError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (!this.shouldCapture(error)) return;

    const sanitizedUrl = sanitizeUrl(request.urlWithParams);
    const statusFamily = error.status === 0 ? '0' : `${Math.floor(error.status / 100)}xx`;
    const errorKind = error.status === 0 ? 'network' : error.status >= 500 ? 'server' : 'client';
    const userBlocking = this.isUserBlocking(error, request);
    const requestHeaders = this.normalizeHeaders(request.headers);
    const responseHeaders = this.normalizeHeaders(error.headers);
    const requestBody = this.sanitizePayload(request.body);
    const responseBody = this.sanitizePayload(error.error);

    Sentry.withScope((scope) => {
      const errorForSentry = this.buildHttpError(error, request, sanitizedUrl);

      scope.setLevel('error');
      scope.setTag(SENTRY_TAG_KEYS.httpStatus, String(error.status));
      scope.setTag(SENTRY_TAG_KEYS.httpStatusFamily, statusFamily);
      scope.setTag(SENTRY_TAG_KEYS.httpErrorKind, errorKind);
      scope.setTag(SENTRY_TAG_KEYS.userBlocking, String(userBlocking));
      scope.setTag(SENTRY_TAG_KEYS.httpMethod, request.method);
      scope.setTag(SENTRY_TAG_KEYS.httpUrl, sanitizedUrl);

      const requestId =
        request.headers.get(SENTRY_HEADER_KEYS.requestId) ??
        request.headers.get(SENTRY_HEADER_KEYS.correlationId);
      if (requestId) {
        scope.setTag(SENTRY_TAG_KEYS.httpRequestId, requestId);
      }

      scope.setContext(SENTRY_CONTEXT_KEYS.http, {
        status: error.status,
        statusText: error.statusText,
        url: sanitizedUrl,
        method: request.method,
        request: {
          headers: requestHeaders,
          body: requestBody,
        },
        response: {
          headers: responseHeaders,
          body: responseBody,
        },
      });

      scope.setFingerprint(['http', String(error.status), request.method, sanitizedUrl]);
      Sentry.captureException(errorForSentry);
    });
  }

  private shouldCapture(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= 400;
  }

  private shouldIgnoreError(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (this.isSentryIngestRequest(request.url)) return true;
    if (this.isAbortError(error)) return true;
    return false;
  }

  private isUserBlocking(error: HttpErrorResponse, request: HttpRequest<unknown>): boolean {
    if (error.status === 0 || error.status >= 500) return true;
    if ([401, 403, 404, 409, 422, 429].includes(error.status)) return true;
    if (error.status >= 400 && request.method !== 'GET') return true;
    return false;
  }

  private buildHttpError(
    error: HttpErrorResponse,
    request: HttpRequest<unknown>,
    sanitizedUrl: string
  ): Error {
    const status = error.status || 0;
    const statusText = error.statusText || (status === 0 ? 'Network Error' : 'HTTP Error');
    const message = `[HTTP ${status}] ${request.method} ${sanitizedUrl} (${statusText})`;
    const wrapped = new Error(message);
    wrapped.name = 'HttpError';
    return wrapped;
  }

  private normalizeHeaders(headers: HttpRequest<unknown>['headers'] | null | undefined): Record<string, string> | undefined {
    if (!headers) return undefined;
    const raw: Record<string, string> = {};
    headers.keys().forEach((key) => {
      const values = headers.getAll(key);
      const value = values ? values.join(',') : headers.get(key);
      if (value) {
        raw[key] = value;
      }
    });
    const sanitized = sanitizeHeaders(raw);
    return Object.keys(sanitized).length ? sanitized : undefined;
  }

  private sanitizePayload(payload: unknown): unknown {
    if (payload == null) return undefined;
    if (typeof FormData !== 'undefined' && payload instanceof FormData) {
      const keys: string[] = [];
      payload.forEach((_value, key) => {
        keys.push(key);
      });
      return keys.length ? { formDataKeys: keys } : undefined;
    }
    if (typeof Blob !== 'undefined' && payload instanceof Blob) {
      return { blob: { size: payload.size, type: payload.type || 'application/octet-stream' } };
    }
    if (payload instanceof ArrayBuffer) {
      return { arrayBufferBytes: payload.byteLength };
    }
    if (typeof ProgressEvent !== 'undefined' && payload instanceof ProgressEvent) {
      return { progressEvent: { type: payload.type } };
    }
    return sanitizeRequestData(payload);
  }

  private isAbortError(error: HttpErrorResponse): boolean {
    if (error.status !== 0) return false;
    const rawError = error.error as { name?: string; type?: string; message?: string } | null;
    if (!rawError) return false;
    if (rawError.name === 'AbortError') return true;
    if (rawError.type === 'abort') return true;
    if (typeof rawError.message === 'string' && /abort|canceled|cancelled/i.test(rawError.message)) {
      return true;
    }
    return false;
  }

  private isSentryIngestRequest(url: string): boolean {
    if (!SENTRY_INGEST_HOST) return false;
    try {
      return new URL(url, window.location.origin).host === SENTRY_INGEST_HOST;
    } catch {
      return false;
    }
  }
}

function getSentryIngestHost(dsn?: string): string | null {
  if (!dsn) return null;
  try {
    return new URL(dsn).host;
  } catch {
    return null;
  }
}
